import React, { useState } from 'react';
import * as Linking from 'expo-linking';
import { getIntl } from '../../../../../../../utils/intl';
import usePreRedirectArgs from '../../../../../../../hooks/usePreRedirectArgs';
import useOrganizationPaymentIntegration from '../../../../../../../hooks/api/useOrganizationPaymentIntegration';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../../../v2BaseComponents/MainContentContainer';
import Stack from '../../../../../../v2BaseComponents/Stack';
import Text from '../../../../../../v2BaseComponents/Text';
import { AlertIcon } from '../../../../../../v2BaseComponents/Icon';
import Button from '../../../../../../v2BaseComponents/Button';
import { useIsOrganizationOwner } from '../../../../../../../hooks/api/useOrganization';

const intl = getIntl('client-billing-tab-restricted', {
    header: 'Your Account is Restricted',
    subHeader:
        'Your Stripe account is currently in a restricted state.  This is usually due to missing or outdated information within your Stripe account.  In order to resolve this issue, please visit your Stripe dashboard.  Until the issue is resolved, client invoicing will be disabled.',
    buttonText: 'Manage Stripe Account',
    orgOwnerSubtext: 'Only the organization owner can manage the Stripe account.',
    loaderMessage: 'Connecting to your Stripe account...',
});

export type RestrictedContentOwnProps = {};

export type RestrictedContentProps = RestrictedContentOwnProps;

const RestrictedContent: React.FC<RestrictedContentProps> = (_props) => {
    const [isConnecting, setIsConnecting] = useState(false);
    const isOrganizationOwner = useIsOrganizationOwner();
    const { setPreRedirectArgs } = usePreRedirectArgs();
    const { isRetrievingOnboardingUrl, retrieveOnboardingUrl } = useOrganizationPaymentIntegration({
        onRetrieveOnboardingUrlSuccess: (setupLink) => {
            setPreRedirectArgs(
                {
                    type: 'stripe-connected-account',
                },
                () => {
                    setIsConnecting(true);
                    Linking.openURL(setupLink);
                },
            );
        },
    });

    return (
        <AnimateLoadedContent
            isLoading={isRetrievingOnboardingUrl || isConnecting}
            message={isConnecting ? intl('loaderMessage') : undefined}
        >
            {() => (
                <ActionButtonLayout scrollable>
                    <MainContentContainer style={{ minHeight: '100%', minWidth: '100%' }}>
                        <Stack vertical justify="center" align="center" space={20} fullHeight fullWidth>
                            <Stack vertical justify="center" align="center" space={4}>
                                <Text variant="bold-h4">{intl('header')}</Text>
                                <AlertIcon size="lg" name="AlertTriangle" variant="warning" />
                                <Text variant="body-1" alignText="center">
                                    {intl('subHeader')}
                                </Text>
                            </Stack>
                            <Stack vertical justify="center" align="center">
                                <Button
                                    title={intl('buttonText')}
                                    icon="Settings"
                                    onPress={retrieveOnboardingUrl}
                                    variant="primary"
                                    size="lg"
                                    testID="resolve-restricted-account-stripe-button"
                                    isDisabled={!isOrganizationOwner}
                                />
                                {!isOrganizationOwner && (
                                    <Text variant="caption-2" alignText="center" color="Gray-Text">
                                        {intl('orgOwnerSubtext')}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(RestrictedContent);
